import React, { useEffect, useState } from 'react';
import { chatService } from '../services/chatService';
import { useChatStore } from '../store/chatStore';
import { ChatSessionSummary } from '../types/chat';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { EmptyState } from '../components/common/EmptyState';

/**
 * Chat History Page
 * Browses past AI Assistant conversations (GET /api/v1/chat/sessions) and lets the
 * user resume one by loading it back into the chat panel.
 */
export const ChatHistoryPage: React.FC = () => {
  const { loadSession, togglePanel } = useChatStore();
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSessions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await chatService.getSessions();
      setSessions(response.sessions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load chat history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleResume = async (sessionId: string) => {
    await loadSession(sessionId);
    togglePanel();
  };

  const formatDate = (iso: string): string => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading chat history..." />;
  }

  if (error) {
    return (
      <ErrorState title="Failed to load chat history" message={error} onRetry={fetchSessions} />
    );
  }

  return (
    <div className="space-y-6">
      {sessions.length === 0 ? (
        <EmptyState
          title="No conversations yet"
          description="Start a chat using the assistant icon in the header to see your history here."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm dark:border-border dark:bg-card">
          <ul className="divide-y divide-border">
            {sessions.map((session) => (
              <li key={session.id}>
                <button
                  onClick={() => handleResume(session.id)}
                  className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left hover:bg-muted/30 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring dark:hover:bg-secondary"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {session.preview || 'New conversation'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {session.messageCount} message{session.messageCount === 1 ? '' : 's'} · last
                      active {formatDate(session.lastActiveAt)}
                    </p>
                  </div>
                  <span className="flex-none rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary dark:bg-primary/10 dark:text-primary">
                    Resume
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
